import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';

import Breadcrumbs from 'components/generic/Breadcrumbs';
import AiAssistantCard from 'components/requirements/AiAssistantCard';
import CrewMatchListings from 'components/requirements/CrewMatchListings';
import MatchmakerLoader from 'components/requirements/MatchmakerLoader';
import PageTitle from '../components/generic/PageTitle';
import QuickActionCard from '../components/generic/QuickActions';
import CrewDetailsModal from 'components/crewManagement/CrewDetailsModal';

import { postAssignMatch, postCrewMatch } from 'lib/api/requirementsApi';

const AiMatchmakerPage = () => {
  const navigate = useNavigate();
  const { currentProject, loading: contextLoading, matchedCrew, matchCrew, assignCrew, error } = useProject();

  const [selectedCrew, setSelectedCrew] = useState([]);
  const hasAttemptedMatch = useRef(false);
  const [findMatchParams, setFindMatchParams] = useState({});
  const [equipmentNeeded, setEquipmentNeeded] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [crewMatch, setCrewMatch] = useState([]);
  const [loading, setLoading] = useState(false);

  const [viewCrew, setViewCrew] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Navigation guard: redirect if no current project
 useEffect(() => {
  // 1. If context is still talking to the API, wait.
  if (contextLoading) return;

  // 2. If we have the project, set the params
  if (currentProject) {
    const crewRoles = Array.isArray(currentProject.crew_roles)
      ? currentProject.crew_roles
      : JSON.parse(currentProject.crew_roles || "[]");

    const requiredSkills = Array.isArray(currentProject.required_skills || currentProject.skills_needed)
      ? (currentProject.required_skills || currentProject.skills_needed)
      : JSON.parse(currentProject.required_skills || currentProject.skills_needed || "[]");

    setFindMatchParams({
      crew_roles: crewRoles.map(str => parseInt(str, 10)),
      required_skills: requiredSkills.map(str => parseInt(str, 10)),
    });
    setEquipmentNeeded(currentProject.equipments_needed);
    setProjectId(currentProject.id || currentProject.stream_project_booking_id);
  }
  // 3. ONLY redirect if loading is finished AND we still have no project
  else if (!contextLoading) {
    console.log("No current project found after loading, redirecting...");
    navigate('/requirements');
  }
}, [currentProject, contextLoading, navigate]);

  // Match crew on mount if not already matched (ONLY ONCE)
  useEffect(() => {
    console.log("=== AI MATCHMAKER: Crew Matching ===");
    console.log("findMatchParams:", findMatchParams);
    console.log("hasAttemptedMatch:", hasAttemptedMatch.current);

    if (findMatchParams.hasOwnProperty("crew_roles") && !hasAttemptedMatch.current) {
      console.log("Starting crew match API call...");
      hasAttemptedMatch.current = true;
      setLoading(true);
      postCrewMatch(findMatchParams)
        .then(response => {
          console.log("Crew match response:", response);
          console.log("Matched crew count:", response.data?.length || 0);
          setCrewMatch(response.data)
        })
        .catch(err => {
          console.error(`Error in fetch crew match:`, err);
          console.error("Error details:", err.response || err.message);
        })
        .finally(() => {
          console.log("Crew match loading complete");
          setLoading(false);
        })
    } else {
      console.log("Skipping crew match - already attempted or missing crew_roles");
    }
  }, [findMatchParams]);

  const handleCrewSelection = (id, isSelected) => {
    setSelectedCrew(prev =>
      isSelected ? [...prev, id] : prev.filter(item => item !== id)
    );
  };

  const handleAssignCrew = async () => {
    if (!currentProject || selectedCrew.length === 0) return;

    try {
      // 1️⃣ Read user from localStorage
      const userStr = localStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : null;

      // 2️⃣ Build base payload
      const payload = {
        project_id: projectId,
        assigned_crew: selectedCrew,
      };

      if (user?.organizationType === 2) {
        payload.organization_type = 2;
      }

      const response = await postAssignMatch(payload);

      console.log("Assign Match Response:", response);
      console.log("Equipment Needed:", equipmentNeeded);

      if (!response.error) {
        navigate("/requirements/ai-matchmaker/select-equipment", {
          state: {
            project_id: projectId,
            equipment_needed: equipmentNeeded,
            selectedCrew: selectedCrew,
            event_date: currentProject.event_date,
          },
        });
      }
    } catch (err) {
      console.error("Error assigning match:", err);
    }
  };


  const openCrewModal = (crew) => {
    setViewCrew(crew);
    setShowModal(true);
  };


   if (contextLoading && !currentProject) {
    return <MatchmakerLoader />;
  }
  // Display project name from context or fallback
  const projectName = currentProject?.project_name || "Project";

  return (
    <div className="requirements-content p-6">
      <Breadcrumbs />
      <>
        <PageTitle title={"AI Matchmaker"} description={`We found the perfect team for "${projectName}"`} />

        <CrewDetailsModal
          crew={viewCrew}
          open={showModal}
          onOpenChange={setShowModal}
        />
        {
          loading ? (
            <MatchmakerLoader />
          ) : (
            <>
              <div className="flex flex-col md:flex-row gap-6 md:self-stretch ">
                <div className="md:w-3/4">
                  {
                    crewMatch && crewMatch.length > 0 ? (
                      <CrewMatchListings
                        matchedCrew={crewMatch}
                        selectedCrewIds={selectedCrew}
                        onCrewSelection={handleCrewSelection}
                        openCrewModal={openCrewModal}
                      />
                    ) : (
                      <div className="flex flex-col grow items-center justify-center bg-transparent p-5 ">
                        <p>No matches found</p>
                      </div>
                    )
                  }
                </div>
                <div className="md:w-1/4">
                  <div className="sticky top-6 flex flex-col justify-start items-start gap-6">
                    <QuickActionCard
                      handleClick={handleAssignCrew}
                      buttonText={`Assign Selected Crew (${selectedCrew.length.toString().padStart(2, '0')})`}
                      disabled={selectedCrew.length === 0 || loading}
                    />
                    <AiAssistantCard />
                  </div>
                </div>
              </div>
            </>
          )}
      </>
    </div>
  );
};

export default AiMatchmakerPage;
